import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Block,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PictureAsPdfIcon,
  VpnKey as KeyIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useModulePermissions } from '../hooks/useModulePermissions';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import {
  generateQuestionPaperService,
  GeneratedQuestionPaper,
  QuestionPaperSection,
  QuestionPaperQuestion,
  PaperStatus,
} from '../services/generateQuestionPaperService';
import {
  questionPaperService,
  QuestionBank,
  Chapter,
  Question,
  QuestionType,
  QuestionImportance,
} from '../services/questionPaperService';
import type { PaperNumbering } from '../services/generateQuestionPaperService';
import { DEFAULT_PAPER_NUMBERING, MarksDisplayMode } from '../utils/paperNumbering';
import {
  buildSectionRenderItems,
  sectionEffectiveMarks,
  sectionMarksAreUniform,
} from '../utils/paperLayout';
import { classService } from '../services/classService';
import { centerService } from '../services/centerService';
import { subjectService } from '../services/subjectService';
import { teachingLoadsApi } from '../services/sessions/teachingLoadsApi';
import { Class, Subject, Center } from '../types';

// Sub-components
import PaperListView from '../components/generateQuestionPaper/PaperListView';
import PaperDetailsStep from '../components/generateQuestionPaper/PaperDetailsStep';
import SectionEditor from '../components/generateQuestionPaper/SectionEditor';
import ReviewStep from '../components/generateQuestionPaper/ReviewStep';
import CustomQuestionDialog from '../components/generateQuestionPaper/CustomQuestionDialog';
import SyncPreviewDialog from '../components/generateQuestionPaper/SyncPreviewDialog';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import type { ResyncAllResponse } from '../services/generateQuestionPaperService';

const defaultPaperForm = {
  classId: '',
  subjectId: '',
  title: '',
  description: '',
  instructions: '',
  totalMarks: 100,
  duration: 180,
  examDate: '',
  textDirection: 'ltr' as 'ltr' | 'rtl',
  marksDisplayMode: 'question' as MarksDisplayMode,
  numbering: { ...DEFAULT_PAPER_NUMBERING } as PaperNumbering,
};

type PaperForm = typeof defaultPaperForm;

/** The builder form for a saved paper. */
const formFromPaper = (paper: GeneratedQuestionPaper): PaperForm => ({
  classId: typeof paper.classId === 'string' ? paper.classId : paper.classId._id,
  subjectId: typeof paper.subjectId === 'string' ? paper.subjectId : paper.subjectId._id,
  title: paper.title,
  description: paper.description || '',
  instructions: paper.instructions || '',
  totalMarks: paper.totalMarks,
  duration: paper.duration,
  examDate: (paper.examDate || '').slice(0, 10),
  textDirection: paper.textDirection || 'ltr',
  marksDisplayMode: paper.marksDisplayMode || 'question',
  numbering: paper.numbering ? { ...DEFAULT_PAPER_NUMBERING, ...paper.numbering } : { ...DEFAULT_PAPER_NUMBERING },
});

/** What "unsaved changes" compares against: the form and sections as last loaded or saved. */
const snapshotOf = (form: PaperForm, sections: QuestionPaperSection[]): string => JSON.stringify({ form, sections });

/** The address of an open paper, from its key: 'new', 'edit:<id>' or 'view:<id>'. */
const paramsFor = (key: string): Record<string, string> => {
  if (key === 'new') return { paper: 'new' };
  const [mode, id] = key.split(':');
  return mode === 'view' ? { paper: id, mode } : { paper: id };
};

const GenerateQuestionPaperPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const { user, decodedToken } = useAuth();

  // State - Papers List
  const [papers, setPapers] = useState<GeneratedQuestionPaper[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [centers, setCenters] = useState<Center[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<PaperStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Builder View State
  const [viewMode, setViewMode] = useState<'list' | 'builder'>('list');
  const [editingPaper, setEditingPaper] = useState<GeneratedQuestionPaper | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const savedSnapshot = useRef<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Inline-loader state for the View Paper header download buttons. Kept
  // separate per button so the two downloads can run independently (no
  // contrived mutual-exclusion). The 3-dot menu in the list view still uses
  // the snackbar pattern — those rows have no place for an inline spinner.
  const [downloadingPaperPdf, setDownloadingPaperPdf] = useState(false);
  const [downloadingKeyPdf, setDownloadingKeyPdf] = useState(false);

  // Paper Form Data — includes new Phase-1 spine fields.
  const [paperForm, setPaperForm] = useState({
    classId: '',
    subjectId: '',
    title: '',
    description: '',
    instructions: '',
    totalMarks: 100,
    duration: 180,
    examDate: '',
    textDirection: 'ltr' as 'ltr' | 'rtl',
    marksDisplayMode: 'question' as MarksDisplayMode,
    numbering: { ...DEFAULT_PAPER_NUMBERING } as PaperNumbering,
  });

  const [sections, setSections] = useState<QuestionPaperSection[]>([]);
  const [expandedSection, setExpandedSection] = useState<number | false>(false);

  // Question Bank & Chapter data for builder
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);

  // Custom Question Dialog State
  const [customQuestionDialogOpen, setCustomQuestionDialogOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number | null>(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [customQuestionInitialData, setCustomQuestionInitialData] = useState<Partial<QuestionPaperQuestion> | null>(null);
  // When set, the next custom-question save creates a child under this
  // (sectionIndex, parentQuestionIndex) rather than a top-level question.
  const [pendingSubParent, setPendingSubParent] = useState<{ sectionIndex: number; parentIndex: number } | null>(null);
  // Paper-question ids with an in-flight "Sync from bank" request. Kept as
  // a Set so the card can flip its icon into the spinning state without
  // re-rendering unrelated rows.
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

  // Preview dialog state for the paper-level "Sync from Bank" flow. The
  // dialog opens instantly on click (loading state) while the dry-run
  // runs in the background; once the preview resolves, it swaps to the
  // populated state. Applying runs in the same dialog with the Apply
  // button flipped to a spinner — authors see the preview content right
  // until the apply completes.
  const [syncPreviewOpen, setSyncPreviewOpen] = useState(false);
  const [syncPreviewPaper, setSyncPreviewPaper] = useState<GeneratedQuestionPaper | null>(null);
  const [syncPreview, setSyncPreview] = useState<ResyncAllResponse | null>(null);
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false);
  const [syncPreviewApplying, setSyncPreviewApplying] = useState(false);
  const [syncPreviewError, setSyncPreviewError] = useState<string | null>(null);

  // Confirmation Dialog States
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => Promise<void> | void>(() => {});
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const isSystemAdmin = user?.isSystemAdmin;

  // Plain-teacher scope — mirrors the backend util. True iff the user holds
  // the system Teacher role AND is not elevated to a school / system admin.
  // Admins see every paper + every class / subject in the dropdowns.
  const isRestrictedTeacher = !!decodedToken?.isTeacher
    && !decodedToken?.isSchoolAdmin
    && !decodedToken?.isSystemAdmin;

  // (classId, subjectId) pairs the teacher is assigned to teach in the
  // active session. Populated once on mount for teachers; stays empty for
  // admins. Drives the dropdown filters below.
  const [teacherPairs, setTeacherPairs] = useState<Array<{ classId: string; subjectId: string }>>([]);
  const canViewModule = canRead('generate_question_paper');
  const canEditModule = canWrite('generate_question_paper');

  // ============================================================
  // Memoized values
  // ============================================================

  // Per-section layouts drive both `currentMarks` (paper total) and
  // `nonUniformSelectionSections` (publish gate). Sharing the same builder
  // keeps the header total, section subtotals, and PDF export in agreement.
  const sectionLayouts = useMemo(
    () => sections.map((section, sIdx) => buildSectionRenderItems({
      section: { _id: section._id || `s-${sIdx}` },
      questions: (section.questions || []) as any[],
      startCounter: 0,
    })),
    [sections],
  );

  const currentMarks = useMemo(() => {
    // Layout util collapses OR-alternatives to a single contribution, treats
    // parent-of-composite as its children's sum, and — when `selectionRule` is
    // set — reduces the section total to N items (attempt any N of M).
    //
    // We respect an explicit `section.marks` override first so the paper
    // total agrees with the per-section display ("(24 MARKS)") and with the
    // "6 × 32 ÷ 8 = 24" calculation the user sees next to the Attempt-any
    // field. Without this, Attempt-any sections double-count the
    // suppressed questions and the header total drifts above what any
    // student can actually score.
    let total = 0;
    sections.forEach((section, sIdx) => {
      const items = sectionLayouts[sIdx]?.items || [];
      if (typeof section.marks === 'number' && section.marks > 0) {
        total += section.marks;
      } else {
        total += sectionEffectiveMarks(items, section.selectionRule || null);
      }
    });
    return total;
  }, [sections, sectionLayouts]);

  // Sections with `selectionRule` (attempt N of M) must have equal marks
  // across items — otherwise the total is non-deterministic. Publish is
  // blocked while this list is non-empty; the section editor also surfaces
  // an inline error next to the rule fields.
  const nonUniformSelectionSections = useMemo(() => {
    const bad: string[] = [];
    sections.forEach((section, sIdx) => {
      if (!section.selectionRule?.attemptCount) return;
      const items = sectionLayouts[sIdx]?.items || [];
      if (items.length > 0 && !sectionMarksAreUniform(items)) {
        bad.push(section.name || `Section ${sIdx + 1}`);
      }
    });
    return bad;
  }, [sections, sectionLayouts]);

  const totalMarksExceeded = useMemo(() => {
    return paperForm.totalMarks > 0 && currentMarks > paperForm.totalMarks;
  }, [paperForm.totalMarks, currentMarks]);

  // ─── Teacher-scoped dropdown options ───────────────────────────────────
  // Admins (unrestricted) see every class and every subject the school owns.
  // Plain teachers see only the ones they're currently assigned to teach,
  // with the subject dropdown narrowing further based on the selected class
  // (so a teacher assigned to 11 Physics and 12 Chemistry doesn't see
  // Chemistry listed when they've selected class 11). Builder and list
  // filters share the same rule — one teacher scope, consistent surface.

  const allowedClassIdsSet = useMemo(
    () => new Set(teacherPairs.map(p => p.classId)),
    [teacherPairs],
  );

  const subjectIdsForClass = useMemo(() => {
    // selectedClassId → Set<subjectId> the teacher teaches for that class.
    const map = new Map<string, Set<string>>();
    for (const p of teacherPairs) {
      if (!map.has(p.classId)) map.set(p.classId, new Set());
      map.get(p.classId)!.add(p.subjectId);
    }
    return map;
  }, [teacherPairs]);

  const allSubjectIdsTaughtByMe = useMemo(
    () => new Set(teacherPairs.map(p => p.subjectId)),
    [teacherPairs],
  );

  // Builder — classes available for the paper's classId picker.
  const visibleBuilderClasses = useMemo(() => (
    isRestrictedTeacher ? classes.filter(c => allowedClassIdsSet.has(c._id)) : classes
  ), [classes, isRestrictedTeacher, allowedClassIdsSet]);

  // Builder — subjects for the picker depend on which class is selected.
  const visibleBuilderSubjects = useMemo(() => {
    if (!isRestrictedTeacher) return subjects;
    const allowedIds = subjectIdsForClass.get(paperForm.classId) ?? new Set<string>();
    return subjects.filter(s => allowedIds.has(s._id));
  }, [subjects, isRestrictedTeacher, subjectIdsForClass, paperForm.classId]);

  // List filters — class dropdown mirrors the builder's restriction.
  const visibleFilterClasses = useMemo(() => (
    isRestrictedTeacher ? classes.filter(c => allowedClassIdsSet.has(c._id)) : classes
  ), [classes, isRestrictedTeacher, allowedClassIdsSet]);

  // List filters — subject dropdown narrows by selectedClass if the user has
  // picked one; otherwise shows the union of subjects they teach across all
  // classes (so a teacher scanning without a class filter still sees options).
  const visibleFilterSubjects = useMemo(() => {
    if (!isRestrictedTeacher) return subjects;
    const allowedIds = selectedClass
      ? (subjectIdsForClass.get(selectedClass) ?? new Set<string>())
      : allSubjectIdsTaughtByMe;
    return subjects.filter(s => allowedIds.has(s._id));
  }, [subjects, isRestrictedTeacher, subjectIdsForClass, allSubjectIdsTaughtByMe, selectedClass]);

  // ============================================================
  // Fetch Functions
  // ============================================================

  const fetchCenters = useCallback(async () => {
    if (!isSystemAdmin) return;
    try {
      const response = await centerService.getCenters();
      setCenters(response.centers || []);
    } catch (error) {
      console.error('Failed to fetch centers:', error);
    }
  }, [isSystemAdmin]);

  const fetchClasses = useCallback(async () => {
    try {
      const params: any = { isActive: true, limit: 100 };
      if (selectedCenter && isSystemAdmin) {
        params.centerId = selectedCenter;
      }
      const response = await classService.getClasses(params);
      setClasses(response.classes || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  }, [selectedCenter, isSystemAdmin]);

  const fetchSubjects = useCallback(async (classId?: string) => {
    const targetClassId = classId || selectedClass;
    if (!targetClassId) return;
    try {
      // Subjects are school-wide now; per-class binding lives in Curriculum.
      // Question paper generation falls back to the full subject list for
      // the school — picking a subject not in the class's curriculum is
      // still valid (you can author a paper from any subject in the school).
      const response = await subjectService.getSubjects({
        isActive: true,
        limit: 100,
      });
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  }, [selectedClass]);

  const fetchPapers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (selectedCenter && isSystemAdmin) params.schoolId = selectedCenter;
      if (selectedClass) params.classId = selectedClass;
      if (selectedSubject) params.subjectId = selectedSubject;
      if (selectedStatus) params.status = selectedStatus;
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;

      const response = await generateQuestionPaperService.getGeneratedQuestionPapers(params);
      setPapers(response.papers || []);
      setTotalCount(response.pagination?.totalItems || 0);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch question papers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedCenter, selectedClass, selectedSubject, selectedStatus, debouncedSearchQuery, isSystemAdmin, enqueueSnackbar]);

  const fetchQuestionBanks = useCallback(async () => {
    if (!paperForm.classId) return;
    try {
      const params: any = { classId: paperForm.classId };
      if (paperForm.subjectId) params.subjectId = paperForm.subjectId;
      const response = await questionPaperService.getQuestionBanks(params);
      setQuestionBanks(response.questionBanks || []);
    } catch (error) {
      console.error('Failed to fetch question banks:', error);
    }
  }, [paperForm.classId, paperForm.subjectId]);

  const fetchChapters = useCallback(async () => {
    if (paperForm.classId && paperForm.subjectId) {
      try {
        const chaptersData = await questionPaperService.getChaptersByClassAndSubject(
          paperForm.classId,
          paperForm.subjectId
        );
        setChapters(chaptersData || []);
      } catch (error) {
        console.error('Failed to fetch chapters:', error);
        setChapters([]);
      }
    } else {
      setChapters([]);
    }
  }, [paperForm.classId, paperForm.subjectId]);

  // ============================================================
  // Effects
  // ============================================================

  useEffect(() => {
    if (isSystemAdmin) fetchCenters();
  }, [isSystemAdmin, fetchCenters]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) fetchSubjects();
  }, [selectedClass, fetchSubjects]);

  useEffect(() => {
    if (canViewModule) fetchPapers();
  }, [canViewModule, fetchPapers]);

  useEffect(() => {
    if (viewMode === 'builder' && paperForm.classId) fetchQuestionBanks();
  }, [viewMode, fetchQuestionBanks, paperForm.classId]);

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  // Load the teacher's current-session (class, subject) assignments once on
  // mount. We reuse the existing /teaching-loads/mine endpoint (also used by
  // the "My Teaching Loads" page) instead of adding a bespoke /my-scope —
  // same data, DRY. Admins skip the call entirely.
  useEffect(() => {
    if (!isRestrictedTeacher) return;
    let cancelled = false;
    (async () => {
      try {
        const mine = await teachingLoadsApi.getMine();
        if (cancelled) return;
        // De-dupe on (classId, subjectId) — the raw endpoint returns one row
        // per section instance, but paper visibility is at class+subject only.
        const seen = new Set<string>();
        const pairs: Array<{ classId: string; subjectId: string }> = [];
        for (const row of mine.subjectsTaught || []) {
          const key = `${row.classId}:${row.subjectId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          pairs.push({ classId: row.classId, subjectId: row.subjectId });
        }
        setTeacherPairs(pairs);
      } catch (err) {
        console.error('Failed to fetch teacher assignments:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isRestrictedTeacher]);

  // ============================================================
  // Handlers - Paper Management
  // ============================================================



  // Which paper is open lives in the address (?paper=new, ?paper=<id>,
  // ?paper=<id>&mode=view), so the browser's Back and Forward move between
  // the list and a paper, and a refresh keeps the paper open.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routePaper = searchParams.get('paper');
  const routeMode: 'edit' | 'view' = searchParams.get('mode') === 'view' ? 'view' : 'edit';
  const routeKey = routePaper ? (routePaper === 'new' ? 'new' : `${routeMode}:${routePaper}`) : null;
  /** The paper the builder holds, in the same form as `routeKey`. */
  const openKey = useRef<string | null>(null);
  /** Set while the page itself is closing the paper, so that isn't mistaken for the browser's Back. */
  const leaving = useRef(false);
  const cameFromList = !!(location.state as { fromList?: boolean } | null)?.fromList;

  const openPaper = useCallback((key: string) => {
    setSearchParams(paramsFor(key), { state: { fromList: true } });
  }, [setSearchParams]);

  const handleCreateNew = useCallback(() => openPaper('new'), [openPaper]);
  const handleEdit = useCallback((paper: GeneratedQuestionPaper) => openPaper(`edit:${paper._id}`), [openPaper]);
  const handleView = useCallback((paper: GeneratedQuestionPaper) => openPaper(`view:${paper._id}`), [openPaper]);

  const startNewPaper = useCallback(() => {
    setEditingPaper(null);
    setIsViewMode(false);
    const form = { ...defaultPaperForm, classId: selectedClass || '', subjectId: selectedSubject || '' };
    setPaperForm(form);
    setSections([]);
    savedSnapshot.current = snapshotOf(form, []);
    setActiveStep(0);
    setViewMode('builder');
  }, [selectedClass, selectedSubject]);

  /** Opens a saved paper. Only drafts can be edited; anything else opens for viewing. */
  const loadPaper = useCallback(async (id: string, mode: 'edit' | 'view') => {
    try {
      setLoading(true);
      const data = await generateQuestionPaperService.getGeneratedQuestionPaperById(id);
      if (!data) throw new Error('Failed to load question paper');

      const editable = mode === 'edit' && canEditModule && data.paper.status === PaperStatus.DRAFT;
      if (mode === 'edit' && !editable) {
        enqueueSnackbar('Only draft papers can be edited. Opened for viewing.', { variant: 'info' });
        openKey.current = `view:${id}`;
        setSearchParams({ paper: id, mode: 'view' }, { replace: true, state: location.state });
      }

      const form = formFromPaper(data.paper);
      setIsViewMode(!editable);
      setEditingPaper(data.paper);
      setPaperForm(form);
      await fetchSubjects(form.classId);
      setSections(data.sections || []);
      savedSnapshot.current = snapshotOf(form, data.sections || []);
      setActiveStep(editable ? 0 : 2);
      setViewMode('builder');
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load question paper', { variant: 'error' });
      openKey.current = null;
      setSearchParams({}, { replace: true });
    } finally {
      setLoading(false);
    }
  }, [canEditModule, enqueueSnackbar, fetchSubjects, setSearchParams, location.state]);

  const closeBuilder = useCallback(() => {
    openKey.current = null;
    setViewMode('list');
    setEditingPaper(null);
    setIsViewMode(false);
    setPaperForm(defaultPaperForm);
    setSections([]);
    // The paper may have been saved, renamed or published while open.
    fetchPapers();
  }, [fetchPapers]);

  /** Back to the list: back through history when the list is where we came from. */
  const leaveBuilder = useCallback(() => {
    leaving.current = true;
    if (cameFromList) navigate(-1);
    else setSearchParams({}, { replace: true });
  }, [cameFromList, navigate, setSearchParams]);

  const hasUnsavedChanges = useCallback(
    () => !isViewMode && snapshotOf(paperForm, sections) !== savedSnapshot.current,
    [isViewMode, paperForm, sections],
  );

  const confirmLeave = useCallback(() => {
    setConfirmTitle('Unsaved Changes');
    setConfirmMessage('Are you sure you want to leave? Any unsaved changes will be lost.');
    setConfirmAction(() => () => {
      setConfirmDialogOpen(false);
      leaveBuilder();
    });
    setConfirmDialogOpen(true);
  }, [leaveBuilder]);

  // Follow the address: open the paper it names, or close the open one.
  useEffect(() => {
    if (routeKey === openKey.current) return;
    if (!routeKey) {
      // The browser's Back with unsaved work: put the paper back and ask first.
      if (!leaving.current && openKey.current && hasUnsavedChanges()) {
        setSearchParams(paramsFor(openKey.current), { state: { fromList: true } });
        confirmLeave();
        return;
      }
      leaving.current = false;
      closeBuilder();
      return;
    }
    openKey.current = routeKey;
    if (routePaper === 'new') startNewPaper();
    else if (routePaper) loadPaper(routePaper, routeMode);
  }, [routeKey, routePaper, routeMode, hasUnsavedChanges, setSearchParams, confirmLeave, closeBuilder, startNewPaper, loadPaper]);

  const handleDelete = useCallback((paper: GeneratedQuestionPaper) => {
    setConfirmTitle('Delete Question Paper');
    setConfirmMessage(`Are you sure you want to delete "${paper.title}"?`);
    setConfirmAction(() => async () => {
      try {
        setConfirmLoading(true);
        await generateQuestionPaperService.deleteGeneratedQuestionPaper(paper._id);
        enqueueSnackbar('Question paper deleted successfully', { variant: 'success' });
        fetchPapers();
        setConfirmDialogOpen(false);
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Failed to delete question paper', { variant: 'error' });
      } finally {
        setConfirmLoading(false);
      }
    });
    setConfirmDialogOpen(true);
  }, [enqueueSnackbar, fetchPapers]);

  const handleDuplicate = useCallback(async (paper: GeneratedQuestionPaper) => {
    try {
      await generateQuestionPaperService.duplicateGeneratedQuestionPaper(paper._id);
      enqueueSnackbar('Question paper duplicated successfully', { variant: 'success' });
      fetchPapers();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to duplicate question paper', { variant: 'error' });
    }
  }, [enqueueSnackbar, fetchPapers]);

  const handleExportPDF = useCallback(async (paper: GeneratedQuestionPaper, includeAnswers: boolean = false) => {
    try {
      enqueueSnackbar('Downloading question paper...', { variant: 'info' });
      await generateQuestionPaperService.exportQuestionPaperPDF(paper._id, includeAnswers);
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to export PDF', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Variant of handleExportPDF used by the View Paper header buttons. Skips
  // the "Downloading..." snackbar (the button shows its own spinner) and
  // tracks state per-button so each can spin independently.
  const handleExportPDFInline = useCallback(async (paper: GeneratedQuestionPaper, includeAnswers: boolean) => {
    const setLoading = includeAnswers ? setDownloadingKeyPdf : setDownloadingPaperPdf;
    setLoading(true);
    try {
      await generateQuestionPaperService.exportQuestionPaperPDF(paper._id, includeAnswers);
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || 'Failed to export PDF',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Archive / unarchive — sends just `{ status }` to the existing update
  // endpoint, which already handles status-only payloads (the sections diff
  // is skipped when `sections` is absent).
  const handleChangeStatus = useCallback(async (paper: GeneratedQuestionPaper, newStatus: PaperStatus) => {
    try {
      await generateQuestionPaperService.updateGeneratedQuestionPaper(paper._id, { status: newStatus } as any);
      const verb = newStatus === PaperStatus.ARCHIVED
        ? 'archived'
        : newStatus === PaperStatus.PUBLISHED
          ? 'restored'
          : 'updated';
      enqueueSnackbar(`Question paper ${verb} successfully`, { variant: 'success' });
      fetchPapers();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update paper status', { variant: 'error' });
    }
  }, [enqueueSnackbar, fetchPapers]);

  /**
   * Paper-level sync — step 1 of 2: open the preview dialog and start the
   * dry-run.
   *
   * Short-circuit paths skip the dialog entirely with just a toast:
   *   • totalWithRef === 0 → "nothing to sync" (paper has no bank-linked rows)
   *   • updated === 0      → "all already in sync" (no diffs found)
   *
   * Otherwise the dialog stays open in populated mode until the user
   * either Cancels or clicks Apply (which calls `handleApplySyncFromBank`).
   */
  const handleSyncPaperFromBank = useCallback(async (paper: GeneratedQuestionPaper) => {
    // Open the dialog IMMEDIATELY so the click feels instant — the
    // dry-run runs in the background and populates the content when it
    // resolves. No awkward dead time between click and dialog.
    setSyncPreviewPaper(paper);
    setSyncPreview(null);
    setSyncPreviewError(null);
    setSyncPreviewLoading(true);
    setSyncPreviewApplying(false);
    setSyncPreviewOpen(true);

    try {
      const result = await generateQuestionPaperService.previewResyncFromBank(paper._id);
      if (!result) throw new Error('Empty preview response');

      const updated      = result.updated ?? 0;
      const totalWithRef = result.totalWithRef ?? 0;
      const unchanged    = result.unchanged ?? 0;
      const dangling     = result.dangling ?? 0;

      // Short-circuit: no bank-linked rows at all → close dialog, toast.
      if (totalWithRef === 0) {
        setSyncPreviewOpen(false);
        setSyncPreviewLoading(false);
        setSyncPreviewPaper(null);
        enqueueSnackbar(
          'Nothing to sync — this paper has no questions linked to the bank.',
          { variant: 'info' },
        );
        return;
      }

      // Short-circuit: everything matches → close dialog with a positive
      // info toast. The author already knows what the paper looks like;
      // no need to show them an empty list.
      if (updated === 0) {
        setSyncPreviewOpen(false);
        setSyncPreviewLoading(false);
        setSyncPreviewPaper(null);
        if (dangling > 0) {
          enqueueSnackbar(
            `${unchanged} already in sync • ${dangling} skipped (source removed from bank).`,
            { variant: 'warning' },
          );
        } else {
          enqueueSnackbar(
            `All ${unchanged} bank-linked question${unchanged === 1 ? '' : 's'} were already in sync — nothing to update.`,
            { variant: 'info' },
          );
        }
        return;
      }

      // Populated preview — dialog stays open with content.
      setSyncPreview(result);
      setSyncPreviewLoading(false);
    } catch (err: any) {
      setSyncPreviewLoading(false);
      setSyncPreviewError(
        err?.response?.data?.message || err?.message || 'Failed to load sync preview',
      );
    }
  }, [enqueueSnackbar]);

  /**
   * Paper-level sync — step 2 of 2: apply the preview the author just
   * saw. Keeps the dialog open with the list visible until the server
   * comes back, so authors see exactly what they're committing.
   */
  const handleApplySyncFromBank = useCallback(async () => {
    const paper = syncPreviewPaper;
    if (!paper) return;

    setSyncPreviewApplying(true);
    try {
      const result = await generateQuestionPaperService.resyncAllFromBank(paper._id);
      const updated   = result?.updated ?? 0;
      const unchanged = result?.unchanged ?? 0;
      const dangling  = result?.dangling ?? 0;

      // Impact-first toast — same matrix as the per-row sync, scaled up.
      if (updated === 0 && dangling === 0) {
        enqueueSnackbar(
          `All ${unchanged} bank-linked question${unchanged === 1 ? '' : 's'} were already in sync.`,
          { variant: 'info' },
        );
      } else if (dangling === 0) {
        enqueueSnackbar(
          `${updated} updated • ${unchanged} already in sync.`,
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar(
          `${updated} updated • ${unchanged} already in sync • ${dangling} skipped (source removed from bank).`,
          { variant: 'warning' },
        );
      }

      // If the author is currently editing this paper, pull the fresh
      // sections so the cards redraw with the synced snapshots.
      if (updated > 0 && editingPaper?._id === paper._id) {
        const refreshed = await generateQuestionPaperService.getGeneratedQuestionPaperById(paper._id);
        if (refreshed?.sections) setSections(refreshed.sections);
      }

      // Close the dialog on success.
      setSyncPreviewOpen(false);
      setSyncPreviewPaper(null);
      setSyncPreview(null);
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message || err?.message || 'Failed to apply sync',
        { variant: 'error' },
      );
    } finally {
      setSyncPreviewApplying(false);
    }
  }, [syncPreviewPaper, editingPaper, enqueueSnackbar]);

  const handleCloseSyncPreview = useCallback(() => {
    if (syncPreviewApplying) return; // ignore while mid-apply
    setSyncPreviewOpen(false);
    setSyncPreviewPaper(null);
    setSyncPreview(null);
    setSyncPreviewError(null);
    setSyncPreviewLoading(false);
  }, [syncPreviewApplying]);

  // ============================================================
  // Handlers - Builder
  // ============================================================

  const handleSavePaper = useCallback(async (status: PaperStatus = PaperStatus.DRAFT) => {
    try {
      if (!paperForm.title || !paperForm.classId || !paperForm.subjectId) {
        enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
        return;
      }

      if (paperForm.totalMarks > 0 && currentMarks > paperForm.totalMarks) {
        enqueueSnackbar(`Total marks (${currentMarks}) exceeds the limit (${paperForm.totalMarks})`, { variant: 'error' });
        return;
      }

      if (status === PaperStatus.PUBLISHED) {
        const hasQuestions = sections.some(section => section.questions && section.questions.length > 0);
        if (!hasQuestions) {
          enqueueSnackbar('Cannot publish: Question paper must have at least one question', { variant: 'error' });
          return;
        }

        if (nonUniformSelectionSections.length > 0) {
          enqueueSnackbar(
            `Cannot publish: sections with "Attempt any N of M" require equal marks across all questions (${nonUniformSelectionSections.join(', ')})`,
            { variant: 'error' }
          );
          return;
        }

        if (paperForm.totalMarks > 0 && currentMarks !== paperForm.totalMarks) {
          enqueueSnackbar(
            `Cannot publish: Current marks (${currentMarks}) do not match total marks (${paperForm.totalMarks}). Please adjust question marks to match.`,
            { variant: 'error' }
          );
          return;
        }
      }

      // Strip empty-string ObjectId fields — Mongoose can't cast "" and will
      // reject the whole write. Empty is the FE's "unset" sentinel; convert to
      // explicit undefined at the wire boundary so validation passes.
      const sectionsToSave = sections.map(section => ({
        ...section,
        questions: section.questions?.map(q => {
          const cleaned: any = {
            ...q,
            questionId: typeof q.questionId === 'object' && q.questionId !== null
              ? (q.questionId as any)._id || q.questionId
              : q.questionId,
          };
          if (cleaned.chapterId === '') delete cleaned.chapterId;
          if (cleaned.difficulty === '') delete cleaned.difficulty;
          return cleaned;
        }),
      }));

      const data = {
        ...paperForm,
        // Persist the computed total — it's the single source of truth
        // once there's at least one question. `paperForm.totalMarks` is
        // the user's TARGET and drives validation (publish is blocked
        // unless `currentMarks === paperForm.totalMarks`), but storing it
        // directly meant a stale load value (set before a section
        // override was added) kept overriding the actual computed total
        // on save. Fall back to the target only when nothing has been
        // added yet, so empty drafts can still satisfy the backend's
        // required-field check.
        totalMarks: currentMarks > 0 ? currentMarks : paperForm.totalMarks,
        sections: sectionsToSave,
      };

      let result;
      if (editingPaper) {
        result = await generateQuestionPaperService.updateGeneratedQuestionPaper(editingPaper._id, { ...data, status });
        enqueueSnackbar('Question paper updated successfully', { variant: 'success' });
      } else {
        result = await generateQuestionPaperService.createGeneratedQuestionPaper(data);
        enqueueSnackbar('Question paper created successfully', { variant: 'success' });
      }

      if (status === PaperStatus.DRAFT) {
        if (result?.paper) {
          setEditingPaper(result.paper);
          if (!editingPaper) {
            // A new paper now has an id: name it in the address so a refresh reopens it.
            openKey.current = `edit:${result.paper._id}`;
            setSearchParams({ paper: result.paper._id }, { replace: true, state: location.state });
          }
        }
        if (result?.sections) {
          setSections(result.sections);
        }
        savedSnapshot.current = snapshotOf(paperForm, result?.sections || sections);
      } else {
        leaveBuilder();
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to save question paper', { variant: 'error' });
    }
  }, [paperForm, currentMarks, nonUniformSelectionSections, sections, editingPaper, enqueueSnackbar, setSearchParams, location.state, leaveBuilder]);

  const handlePublish = useCallback(() => {
    handleSavePaper(PaperStatus.PUBLISHED);
  }, [handleSavePaper]);

  // ============================================================
  // Handlers - Paper Form Update
  // ============================================================

  const handleUpdatePaperForm = useCallback((updates: Partial<typeof paperForm>) => {
    setPaperForm(prev => ({ ...prev, ...updates }));
  }, []);

  // ============================================================
  // Handlers - Sections
  // ============================================================

  const handleAddSection = useCallback(() => {
    const tempKey = `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Count top-level sections for the default name (subsections don't
    // consume the A-B-C sequence).
    const topLevelCount = sections.filter(s => !s.parentSectionId && !s.parentSectionKey).length;
    const newSection: QuestionPaperSection = {
      tempKey,
      name: `Section ${String.fromCharCode(65 + topLevelCount)}`,
      description: '',
      instructions: '',
      order: sections.length,
      questions: [],
    };
    setSections(prev => [...prev, newSection]);
    setExpandedSection(sections.length);
  }, [sections]);

  /**
   * Insert a subsection immediately after its parent (and any of the parent's
   * existing subsections). Uses `tempKey`/`parentSectionKey` for stable
   * parent-child linkage on the client; the BE resolves both to real _ids
   * on save.
   */
  const handleAddSubsection = useCallback((parentIndex: number) => {
    setSections(prev => {
      const parent = prev[parentIndex];
      if (!parent) return prev;
      const parentKey = String(parent.tempKey || parent._id || '');
      if (!parentKey) return prev;

      // Insert AFTER the last existing subsection of this parent.
      let insertAt = parentIndex + 1;
      while (
        insertAt < prev.length &&
        (prev[insertAt].parentSectionKey === parentKey || prev[insertAt].parentSectionId === parentKey)
      ) {
        insertAt += 1;
      }

      const existingSubCount = prev.filter(
        s => s.parentSectionKey === parentKey || s.parentSectionId === parentKey,
      ).length;
      const tempKey = `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const sub: QuestionPaperSection = {
        tempKey,
        parentSectionKey: parentKey,
        name: `${parent.name} — ${String.fromCharCode(97 + existingSubCount)}`, // e.g. "Section A — a"
        description: '',
        instructions: '',
        order: insertAt,
        questions: [],
      };
      const next = [...prev];
      next.splice(insertAt, 0, sub);
      next.forEach((s, i) => { s.order = i; });
      return next;
    });
    enqueueSnackbar('Subsection added. Fill it in below.', { variant: 'info' });
  }, [enqueueSnackbar]);

  const handleUpdateSection = useCallback((index: number, updates: Partial<QuestionPaperSection>) => {
    setSections(prev => {
      const newSections = [...prev];
      newSections[index] = { ...newSections[index], ...updates };
      return newSections;
    });
  }, []);

  const handleDeleteSection = useCallback((index: number) => {
    const target = sections[index];
    const isParent = target && !target.parentSectionKey && !target.parentSectionId;
    const childCount = isParent
      ? sections.filter(s => {
          const key = String(target.tempKey || target._id || '');
          return key && (s.parentSectionKey === key || s.parentSectionId === key);
        }).length
      : 0;
    setConfirmTitle('Delete Section');
    setConfirmMessage(childCount > 0
      ? `This section has ${childCount} subsection${childCount === 1 ? '' : 's'}. Deleting will remove all subsections and their questions too. Continue?`
      : 'Are you sure you want to delete this section? All questions in it will be removed.');
    setConfirmAction(() => () => {
      setSections(prev => {
        const t = prev[index];
        if (!t) return prev;
        const key = String(t.tempKey || t._id || '');
        return prev
          .filter((s, i) => {
            if (i === index) return false;
            // Cascade: drop subsections belonging to a deleted parent.
            if (key && (s.parentSectionKey === key || s.parentSectionId === key)) return false;
            return true;
          })
          .map((s, i) => ({ ...s, order: i }));
      });
      setConfirmDialogOpen(false);
    });
    setConfirmDialogOpen(true);
  }, [sections]);

  const handleMoveSectionUp = useCallback((index: number) => {
    if (index === 0) return;
    setSections(prev => {
      const newSections = [...prev];
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
      newSections.forEach((s, i) => s.order = i);
      return newSections;
    });
  }, []);

  const handleMoveSectionDown = useCallback((index: number) => {
    setSections(prev => {
      if (index === prev.length - 1) return prev;
      const newSections = [...prev];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      newSections.forEach((s, i) => s.order = i);
      return newSections;
    });
  }, []);

  const handleToggleExpandSection = useCallback((index: number) => {
    setExpandedSection(prev => prev === index ? false : index);
  }, []);

  // ============================================================
  // Handlers - Questions
  // ============================================================

  /**
   * Snapshot a picked bank Question onto a fresh paper row. `questionId`
   * is retained as a soft ref so the user can later "Sync from bank" —
   * the row itself carries a full, self-contained snapshot of the
   * renderable fields.
   */
  const snapshotFromBank = (question: Question): Partial<QuestionPaperQuestion> => ({
    questionId: question._id,
    questionText: question.questionText,
    type: question.type,
    importance: question.importance,
    options: question.options,
    correctAnswer: question.correctAnswer,
    answer: question.answer,
    answerImages: (question as any).answerImages,
    questionImages: (question as any).questionImages,
    subParts: question.subParts,
  });

  const handleAddQuestionFromBank = useCallback((sectionIndex: number, question: Question) => {
    const newQuestion: QuestionPaperQuestion = {
      ...snapshotFromBank(question),
      marks: question.metadata?.marks || 1,
      order: sections[sectionIndex]?.questions?.length || 0,
      chapterId: typeof question.chapterId === 'string' ? question.chapterId : question.chapterId._id,
      difficulty: question.metadata?.difficulty,
    } as QuestionPaperQuestion;

    setSections(prev => {
      const newSections = prev.map((s, i) => {
        if (i !== sectionIndex) return s;
        return {
          ...s,
          questions: [...(s.questions || []), newQuestion],
        };
      });
      return newSections;
    });
    enqueueSnackbar('Question added successfully', { variant: 'success' });
  }, [sections, enqueueSnackbar]);

  const handleOpenCustomQuestion = useCallback((sectionIndex: number) => {
    setCurrentSectionIndex(sectionIndex);
    setEditingQuestionIndex(null);
    setCustomQuestionInitialData({
      questionText: '',
      type: QuestionType.MCQ,
      importance: QuestionImportance.MEDIUM,
      options: ['', '', '', ''],
      correctAnswer: '',
      answer: '',
      marks: 1,
      chapterId: '',
      difficulty: '',
    });
    setCustomQuestionDialogOpen(true);
  }, []);

  const handleEditMCQ = useCallback((qIndex: number, sectionIndex: number, q: QuestionPaperQuestion) => {
    setEditingQuestionIndex(qIndex);
    setCurrentSectionIndex(sectionIndex);
    // Snapshot fields live inline on the row. Pass them straight into the
    // dialog — the dialog writes back into the same field names.
    setCustomQuestionInitialData({
      ...q,
      questionText: q.questionText || '',
      type: q.type || QuestionType.MCQ,
      importance: q.importance || QuestionImportance.MEDIUM,
      options: q.options || ['', '', '', ''],
      correctAnswer: q.correctAnswer || '',
      answer: q.answer || '',
      subParts: q.subParts,
      marks: q.marks || 1,
    });
    setCustomQuestionDialogOpen(true);
  }, []);

  const handleSaveCustomQuestion = useCallback((formData: Partial<QuestionPaperQuestion>, editingIdx: number | null) => {
    if (!formData.questionText || currentSectionIndex === null) {
      enqueueSnackbar('Please fill in question text', { variant: 'warning' });
      return;
    }

    // Sub-question path — the dialog was opened via "Add sub-question" on a
    // parent. Stamp the parent + insert this new question as a child right
    // after the parent's existing children.
    const subCtx = pendingSubParent && pendingSubParent.sectionIndex === currentSectionIndex ? pendingSubParent : null;

    setSections(prev => {
      return prev.map((s, i) => {
        if (i !== currentSectionIndex) return s;

        if (editingIdx !== null) {
          const updatedQuestions = [...(s.questions || [])];
          const existing = updatedQuestions[editingIdx];
          // Editing an existing row: overwrite snapshot fields with the
          // dialog's values. Preserve the questionId soft ref so the user
          // can still "Sync from bank" (with the caveat that the button
          // will pull the bank version back, discarding local edits — that
          // is the whole point of the sync UX).
          updatedQuestions[editingIdx] = {
            ...existing,
            ...(formData as QuestionPaperQuestion),
          };
          return { ...s, questions: updatedQuestions };
        }

        if (subCtx) {
          const qs = [...(s.questions || [])];
          const parent = qs[subCtx.parentIndex];
          if (!parent) return s;
          const gid = (parent.subGroupRole === 'parent' && parent.subGroupId) || generateSubGroupId();
          if (parent.subGroupId !== gid || parent.subGroupRole !== 'parent') {
            qs[subCtx.parentIndex] = { ...parent, subGroupId: gid, subGroupRole: 'parent' };
          }
          let insertAt = subCtx.parentIndex + 1;
          while (insertAt < qs.length && qs[insertAt].subGroupId === gid && qs[insertAt].subGroupRole === 'child') {
            insertAt += 1;
          }
          const child: QuestionPaperQuestion = {
            ...(formData as QuestionPaperQuestion),
            order: insertAt,
            subGroupId: gid,
            subGroupRole: 'child',
          };
          qs.splice(insertAt, 0, child);
          qs.forEach((q, idx) => { q.order = idx; });
          return { ...s, questions: qs };
        }

        const newQuestion: QuestionPaperQuestion = {
          ...(formData as QuestionPaperQuestion),
          order: s.questions?.length || 0,
        };
        return { ...s, questions: [...(s.questions || []), newQuestion] };
      });
    });

    enqueueSnackbar(
      editingIdx !== null
        ? 'Question updated successfully'
        : subCtx ? 'Sub-question added successfully' : 'Custom question added successfully',
      { variant: 'success' }
    );
    setCustomQuestionDialogOpen(false);
    setEditingQuestionIndex(null);
    setPendingSubParent(null);
  }, [currentSectionIndex, pendingSubParent, enqueueSnackbar]);

  const handleUpdateQuestionText = useCallback((sectionIndex: number, questionIndex: number, text: string) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const updatedQuestions = [...(s.questions || [])];
      // Inline edit — writes straight to the row's snapshot. Preserves the
      // `questionId` soft ref so "Sync from bank" still works (it will
      // overwrite this local edit, which is the expected sync semantic).
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        questionText: text,
      };
      return { ...s, questions: updatedQuestions };
    }));
  }, []);

  /**
   * Fetch the latest bank version and overwrite this row's snapshot.
   * Handles the three interesting response paths:
   *   • 200  → patch the row in state; toast "synced"
   *   • 410  → source deleted; toast a soft explanation, drop the questionId
   *            so the button hides for this row
   *   • other errors → toast the message; leave the row alone
   */
  const handleResyncQuestionFromBank = useCallback(async (sectionIndex: number, questionIndex: number) => {
    const target = sections[sectionIndex]?.questions?.[questionIndex];
    if (!target?._id || !target.questionId) return;
    const rowId = target._id;

    setSyncingIds(prev => {
      const next = new Set(prev);
      next.add(rowId);
      return next;
    });

    try {
      const result = await generateQuestionPaperService.resyncQuestionFromBank(rowId);
      if (!result?.question) throw new Error('Empty response from server');
      const { question: updated, changed } = result;

      // Patch the row even on `changed: false` — this is a no-op in that
      // case but keeps the local state exactly aligned with the server.
      setSections(prev => prev.map((s, i) => {
        if (i !== sectionIndex) return s;
        const qs = [...(s.questions || [])];
        // Overwrite only snapshot fields — never touch paper-row fields
        // (marks, order, choiceGroupId, subGroupId, etc.). The backend
        // guarantees the same shape.
        qs[questionIndex] = {
          ...qs[questionIndex],
          questionText: updated.questionText,
          type: updated.type,
          importance: updated.importance,
          options: updated.options,
          correctAnswer: updated.correctAnswer,
          answer: updated.answer,
          answerImages: updated.answerImages,
          questionImages: updated.questionImages,
          subParts: updated.subParts,
        };
        return { ...s, questions: qs };
      }));

      // Honest feedback: an "already up to date" message when nothing
      // changed is more useful than a vague success toast.
      enqueueSnackbar(
        changed ? 'Synced with the latest bank version.' : 'Already in sync — no change.',
        { variant: changed ? 'success' : 'info' },
      );
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Sync failed';

      if (status === 410) {
        // Bank source has been removed. Drop the soft ref so the sync
        // button hides for this row; snapshot stays intact.
        setSections(prev => prev.map((s, i) => {
          if (i !== sectionIndex) return s;
          const qs = [...(s.questions || [])];
          qs[questionIndex] = { ...qs[questionIndex], questionId: undefined };
          return { ...s, questions: qs };
        }));
        enqueueSnackbar(message, { variant: 'info' });
      } else {
        enqueueSnackbar(message, { variant: 'error' });
      }
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  }, [sections, enqueueSnackbar]);

  // ============================================================
  // Handlers - Choice groups (OR pairs)
  // ============================================================

  const generateGroupId = () => `cg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  /**
   * Turn `questionIndex` into an OR-pair. If it already has a choiceGroupId
   * we reuse it (adding a 3rd alternative). Otherwise we mint a fresh id and
   * stamp both the source + the new sibling.
   */
  const handleAddOrAlternative = useCallback((sectionIndex: number, questionIndex: number) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const qs = [...(s.questions || [])];
      const source = qs[questionIndex];
      if (!source) return s;
      const gid = source.choiceGroupId || generateGroupId();
      // Blank alternative — the author will edit the text inline. Snapshot
      // fields are minimal (empty text, LONG type) but valid.
      const sibling: QuestionPaperQuestion = {
        questionText: '',
        type: QuestionType.LONG,
        importance: QuestionImportance.MEDIUM,
        marks: source.marks,
        order: source.order + 0.5, // placed between source and next; renumbered below
        choiceGroupId: gid,
      };
      qs[questionIndex] = { ...source, choiceGroupId: gid };
      qs.splice(questionIndex + 1, 0, sibling);
      // Re-normalise `order` to consecutive integers.
      qs.forEach((q, idx) => { q.order = idx; });
      return { ...s, questions: qs };
    }));
    enqueueSnackbar('OR alternative added. Fill in the text and marks.', { variant: 'info' });
  }, [enqueueSnackbar]);

  /**
   * Add one or more bank questions as OR alternatives to the source question.
   * The source is stamped with a `choiceGroupId` (reused if already grouped)
   * and each pick becomes a sibling with the same id, inheriting the
   * source's marks.
   */
  const handleAddOrAlternativesFromBank = useCallback((sectionIndex: number, questionIndex: number, picked: Question[]) => {
    if (!picked.length) return;
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const qs = [...(s.questions || [])];
      const source = qs[questionIndex];
      if (!source) return s;
      const gid = source.choiceGroupId || generateGroupId();
      if (!source.choiceGroupId) qs[questionIndex] = { ...source, choiceGroupId: gid };
      const alternatives: QuestionPaperQuestion[] = picked.map((pq) => ({
        ...snapshotFromBank(pq),
        marks: source.marks,
        order: source.order,
        chapterId: typeof pq.chapterId === 'string' ? pq.chapterId : pq.chapterId._id,
        difficulty: pq.metadata?.difficulty,
        choiceGroupId: gid,
      } as QuestionPaperQuestion));
      qs.splice(questionIndex + 1, 0, ...alternatives);
      qs.forEach((q, idx) => { q.order = idx; });
      return { ...s, questions: qs };
    }));
    enqueueSnackbar(
      `${picked.length} OR alternative${picked.length === 1 ? '' : 's'} added from bank`,
      { variant: 'success' },
    );
  }, [enqueueSnackbar]);

  /**
   * Remove a question from its choice group. If only one member remains
   * after removal, un-group them too — a "group of one" is not a group.
   */
  const handleRemoveFromChoiceGroup = useCallback((sectionIndex: number, questionIndex: number) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const qs = [...(s.questions || [])];
      const target = qs[questionIndex];
      if (!target?.choiceGroupId) return s;
      const gid = target.choiceGroupId;
      qs[questionIndex] = { ...target, choiceGroupId: undefined, choiceGroupLabel: undefined };
      const remaining = qs.filter(q => q.choiceGroupId === gid);
      if (remaining.length === 1) {
        // Auto-collapse — clear the last surviving member's group id.
        const idx = qs.indexOf(remaining[0]);
        qs[idx] = { ...remaining[0], choiceGroupId: undefined, choiceGroupLabel: undefined };
      }
      return { ...s, questions: qs };
    }));
  }, []);

  // ============================================================
  // Handlers - Sub-questions (parent-child grouping)
  // ============================================================

  const generateSubGroupId = () => `sg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  /**
   * Opens the CustomQuestionDialog primed to add a child under this parent.
   * The parent is only stamped with subGroup fields when the user actually
   * saves (in `handleSaveCustomQuestion`) — so cancelling leaves the paper
   * unchanged.
   */
  const handleAddSubQuestion = useCallback((sectionIndex: number, questionIndex: number) => {
    setCurrentSectionIndex(sectionIndex);
    setEditingQuestionIndex(null);
    setPendingSubParent({ sectionIndex, parentIndex: questionIndex });
    setCustomQuestionInitialData({
      questionText: '',
      type: QuestionType.MCQ,
      importance: QuestionImportance.MEDIUM,
      options: ['', '', '', ''],
      correctAnswer: '',
      answer: '',
      marks: 1,
      chapterId: '',
      difficulty: '',
    });
    setCustomQuestionDialogOpen(true);
  }, []);

  /**
   * Insert one or more bank questions as children of `questionIndex`.
   * The parent is stamped (reusing its subGroupId if already a parent), and
   * each pick becomes a child inheriting the picked question's own marks.
   */
  const handleAddSubQuestionsFromBank = useCallback((sectionIndex: number, questionIndex: number, picked: Question[]) => {
    if (!picked.length) return;
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const qs = [...(s.questions || [])];
      const source = qs[questionIndex];
      if (!source) return s;
      const gid = (source.subGroupRole === 'parent' && source.subGroupId) || generateSubGroupId();
      if (source.subGroupId !== gid || source.subGroupRole !== 'parent') {
        qs[questionIndex] = { ...source, subGroupId: gid, subGroupRole: 'parent' };
      }
      let insertAt = questionIndex + 1;
      while (insertAt < qs.length && qs[insertAt].subGroupId === gid && qs[insertAt].subGroupRole === 'child') {
        insertAt += 1;
      }
      const children: QuestionPaperQuestion[] = picked.map((pq) => ({
        ...snapshotFromBank(pq),
        marks: pq.metadata?.marks || 1,
        order: 0, // renumbered below
        chapterId: typeof pq.chapterId === 'string' ? pq.chapterId : pq.chapterId._id,
        difficulty: pq.metadata?.difficulty,
        subGroupId: gid,
        subGroupRole: 'child',
      } as QuestionPaperQuestion));
      qs.splice(insertAt, 0, ...children);
      qs.forEach((q, idx) => { q.order = idx; });
      return { ...s, questions: qs };
    }));
    enqueueSnackbar(
      `${picked.length} sub-question${picked.length === 1 ? '' : 's'} added from bank`,
      { variant: 'success' },
    );
  }, [enqueueSnackbar]);

  /**
   * Un-parent a question. Detaches it from its sub-group. If it's the parent
   * of a group with children, the children are also un-linked (they become
   * standalone top-level questions in the section).
   */
  const handleRemoveFromSubGroup = useCallback((sectionIndex: number, questionIndex: number) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const qs = [...(s.questions || [])];
      const target = qs[questionIndex];
      if (!target?.subGroupId) return s;
      const gid = target.subGroupId;
      if (target.subGroupRole === 'parent') {
        // Disband the whole group — parent + all children unlinked.
        for (let j = 0; j < qs.length; j += 1) {
          if (qs[j].subGroupId === gid) {
            qs[j] = { ...qs[j], subGroupId: undefined, subGroupRole: undefined };
          }
        }
      } else {
        // Detach just this child.
        qs[questionIndex] = { ...target, subGroupId: undefined, subGroupRole: undefined };
        // If the parent now has no children left, unlink the parent too.
        const remainingChildren = qs.filter(q => q.subGroupId === gid && q.subGroupRole === 'child');
        if (remainingChildren.length === 0) {
          const parentIdx = qs.findIndex(q => q.subGroupId === gid && q.subGroupRole === 'parent');
          if (parentIdx >= 0) {
            qs[parentIdx] = { ...qs[parentIdx], subGroupId: undefined, subGroupRole: undefined };
          }
        }
      }
      return { ...s, questions: qs };
    }));
  }, []);

  const handleDeleteQuestion = useCallback((sectionIndex: number, questionIndex: number) => {
    const target = sections[sectionIndex]?.questions?.[questionIndex];
    const isParent = target?.subGroupRole === 'parent';
    setConfirmTitle('Remove Question');
    setConfirmMessage(isParent
      ? 'This question has sub-questions. Removing it will un-link the sub-questions — they will become regular questions in the section. Continue?'
      : 'Are you sure you want to remove this question?');
    setConfirmAction(() => () => {
      setSections(prev => prev.map((s, i) => {
        if (i !== sectionIndex) return s;
        const targetQ = s.questions?.[questionIndex];
        let updatedQuestions = (s.questions || []).filter((_, qi) => qi !== questionIndex);

        // Cascade cleanup for sub-groups.
        if (targetQ?.subGroupRole === 'parent' && targetQ.subGroupId) {
          // Un-link children so they don't hide from the top-level layout.
          const gid = targetQ.subGroupId;
          updatedQuestions = updatedQuestions.map(q =>
            q.subGroupId === gid ? { ...q, subGroupId: undefined, subGroupRole: undefined } : q
          );
        } else if (targetQ?.subGroupRole === 'child' && targetQ.subGroupId) {
          // If this was the last child, un-parent the parent too.
          const gid = targetQ.subGroupId;
          const remainingChildren = updatedQuestions.filter(q => q.subGroupId === gid && q.subGroupRole === 'child');
          if (remainingChildren.length === 0) {
            updatedQuestions = updatedQuestions.map(q =>
              (q.subGroupId === gid && q.subGroupRole === 'parent')
                ? { ...q, subGroupId: undefined, subGroupRole: undefined }
                : q
            );
          }
        }

        updatedQuestions = updatedQuestions.map((q, qi) => ({ ...q, order: qi }));
        return { ...s, questions: updatedQuestions };
      }));
      setConfirmDialogOpen(false);
    });
    setConfirmDialogOpen(true);
  }, [sections]);

  const handleUpdateQuestionMarks = useCallback((sectionIndex: number, questionIndex: number, marks: number) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const updatedQuestions = [...(s.questions || [])];
      if (updatedQuestions[questionIndex]) {
        updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], marks };
      }
      return { ...s, questions: updatedQuestions };
    }));
  }, []);

  // ============================================================
  // List view filter handlers (stable callbacks for PaperListView)
  // ============================================================

  const handleCenterChange = useCallback((value: string) => {
    setSelectedCenter(value);
    setSelectedClass('');
    setSelectedSubject('');
  }, []);

  const handleClassChange = useCallback((value: string) => {
    setSelectedClass(value);
    setSelectedSubject('');
  }, []);

  const handleSubjectChange = useCallback((value: string) => {
    setSelectedSubject(value);
  }, []);

  const handleStatusChange = useCallback((value: PaperStatus | '') => {
    setSelectedStatus(value);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleBackToList = useCallback(() => {
    if (hasUnsavedChanges()) confirmLeave();
    else leaveBuilder();
  }, [hasUnsavedChanges, confirmLeave, leaveBuilder]);

  // ============================================================
  // Permission Check
  // ============================================================

  if (!canViewModule) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h6" fontWeight="500" gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Paper Builder.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // Render
  // ============================================================

  return (
    <Box>
      {viewMode === 'builder' ? (
        <Box>
          {/* Builder Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                {isViewMode ? 'View Paper' : editingPaper ? 'Edit Paper' : 'Paper Builder'}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* When viewing a published / archived paper, expose the two PDF
                  downloads inline — same actions as the 3-dot menu on the
                  list, just one click away while reading the paper. Inline
                  spinners replace the "Downloading…" snackbar the menu uses,
                  matching the pattern in EmployeeAttendancePage's PDF button. */}
              {isViewMode && editingPaper && canViewModule && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Sync from bank — same action as the list menu, surfaced
                      inline while reading the paper so authors don't have to
                      jump back out. Only shown when the user can write. */}
                  {canEditModule && (
                    <Button
                      variant="outlined"
                      startIcon={<SyncIcon />}
                      onClick={() => handleSyncPaperFromBank(editingPaper)}
                      sx={{ borderRadius: 2 }}
                    >
                      Sync from Bank
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={
                      downloadingPaperPdf
                        ? <CircularProgress size={16} thickness={5} color="inherit" />
                        : <PictureAsPdfIcon />
                    }
                    onClick={() => handleExportPDFInline(editingPaper, false)}
                    disabled={downloadingPaperPdf}
                    sx={{ borderRadius: 2 }}
                  >
                    {downloadingPaperPdf ? 'Downloading…' : 'Download Paper'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      downloadingKeyPdf
                        ? <CircularProgress size={16} thickness={5} color="inherit" />
                        : <KeyIcon />
                    }
                    onClick={() => handleExportPDFInline(editingPaper, true)}
                    disabled={downloadingKeyPdf}
                    sx={{ borderRadius: 2 }}
                  >
                    {downloadingKeyPdf ? 'Downloading…' : 'Download with Key'}
                  </Button>
                </Box>
              )}
              {!isViewMode && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* While editing an existing paper, offer bulk sync too.
                      Hidden for a brand-new paper (nothing to sync yet). */}
                  {editingPaper && canEditModule && (
                    <Button
                      variant="outlined"
                      startIcon={<SyncIcon />}
                      onClick={() => handleSyncPaperFromBank(editingPaper)}
                    >
                      Sync from Bank
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSavePaper(PaperStatus.DRAFT)}
                    disabled={!paperForm.title || !paperForm.classId || !paperForm.subjectId}
                  >
                    Save Draft
                  </Button>
                </Box>
              )}
            </Box>
            <Typography variant="body1" color="text.secondary">
              {isViewMode 
                ? 'View the complete question paper details below' 
                : 'Follow the steps below to build your comprehensive question paper'}
            </Typography>
          </Box>

          {/* Stepper - Hide when viewing */}
          {!isViewMode && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Stepper 
                activeStep={activeStep} 
                sx={{ 
                  '& .MuiStepLabel-root .Mui-completed': { color: 'success.main' },
                  '& .MuiStepLabel-root .Mui-active': { color: 'primary.main' },
                  '& .MuiStepLabel-label': { cursor: 'pointer' },
                  '& .MuiStepLabel-label.Mui-active': { color: 'primary.main', fontWeight: 600 },
                  '& .MuiStepLabel-label.Mui-completed': { color: 'text.primary', cursor: 'pointer' },
                  '& .MuiStepIcon-root': { cursor: 'pointer' },
                  '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
                  '& .MuiStepIcon-root.Mui-completed': { color: 'success.main', cursor: 'pointer' },
                }}
              >
                <Step onClick={() => setActiveStep(0)}>
                  <StepLabel sx={{ cursor: 'pointer' }}>Paper Details</StepLabel>
                </Step>
                <Step onClick={() => setActiveStep(1)}>
                  <StepLabel sx={{ cursor: 'pointer' }}>Build Sections</StepLabel>
                </Step>
                <Step onClick={() => setActiveStep(2)}>
                  <StepLabel sx={{ cursor: 'pointer' }}>Review & Publish</StepLabel>
                </Step>
              </Stepper>
            </Paper>
          )}

          {/* Step Content */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
            }}
          >
            {/* Step 1: Paper Details */}
            {activeStep === 0 && !isViewMode && (
              <PaperDetailsStep
                paperForm={paperForm}
                onUpdateForm={handleUpdatePaperForm}
                classes={visibleBuilderClasses}
                subjects={visibleBuilderSubjects}
                onFetchSubjects={fetchSubjects}
                isViewMode={isViewMode}
                currentMarks={currentMarks}
                totalMarksExceeded={totalMarksExceeded}
              />
            )}

            {/* Step 2: Build Sections */}
            {activeStep === 1 && !isViewMode && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Sections & Questions
                  </Typography>
                  {!isViewMode && (
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />} 
                      onClick={handleAddSection}
                      sx={{ borderRadius: 2 }}
                    >
                      Add Section
                    </Button>
                  )}
                </Box>

                {sections.length === 0 ? (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 6, 
                      textAlign: 'center', 
                      bgcolor: 'background.default',
                      borderRadius: 3,
                      border: '2px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No sections yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Start building your paper by adding a section
                    </Typography>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      onClick={handleAddSection}
                    >
                      Add First Section
                    </Button>
                  </Paper>
                ) : (
                  sections.map((section, sectionIndex) => {
                    const parentKey = section.parentSectionKey || section.parentSectionId;
                    const isSub = !!parentKey;
                    const ownKey = String(section.tempKey || section._id || '');
                    const hasChildren = sections.some(
                      s => ownKey && (s.parentSectionKey === ownKey || s.parentSectionId === ownKey),
                    );
                    return (
                      <SectionEditor
                        key={sectionIndex}
                        section={section}
                        sectionIndex={sectionIndex}
                        isExpanded={expandedSection === sectionIndex}
                        isViewMode={isViewMode}
                        isFirst={sectionIndex === 0}
                        isLast={sectionIndex === sections.length - 1}
                        canEdit={canEditModule}
                        questionBanks={questionBanks}
                        chapters={chapters}
                        questionNumberStyle={paperForm.numbering?.questionStyle}
                        mcqOptionStyle={paperForm.numbering?.mcqOptionStyle}
                        isSubsection={isSub}
                        hasSubsections={hasChildren}
                        onToggleExpand={handleToggleExpandSection}
                        onUpdateSection={handleUpdateSection}
                        onDeleteSection={handleDeleteSection}
                        onMoveSectionUp={handleMoveSectionUp}
                        onMoveSectionDown={handleMoveSectionDown}
                        onAddSubsection={handleAddSubsection}
                        onAddQuestionFromBank={handleAddQuestionFromBank}
                        onOpenCustomQuestion={handleOpenCustomQuestion}
                        onUpdateQuestionMarks={handleUpdateQuestionMarks}
                        onDeleteQuestion={handleDeleteQuestion}
                        onEditMCQ={handleEditMCQ}
                        onUpdateQuestionText={handleUpdateQuestionText}
                        subPartStyle={paperForm.numbering?.subPartStyle}
                        onAddOrAlternative={handleAddOrAlternative}
                        onAddOrAlternativesFromBank={handleAddOrAlternativesFromBank}
                        onRemoveFromChoiceGroup={handleRemoveFromChoiceGroup}
                        onAddSubQuestion={handleAddSubQuestion}
                        onAddSubQuestionsFromBank={handleAddSubQuestionsFromBank}
                        onRemoveFromSubGroup={handleRemoveFromSubGroup}
                        onResyncQuestionFromBank={handleResyncQuestionFromBank}
                        syncingIds={syncingIds}
                      />
                    );
                  })
                )}
              </Box>
            )}

            {/* Step 3: Review */}
            {activeStep === 2 && (
              <ReviewStep
                paperForm={paperForm}
                sections={sections}
                isViewMode={isViewMode}
                currentMarks={currentMarks}
              />
            )}

            {/* Navigation Buttons - Hide when viewing */}
            {!isViewMode && (
              <Box display="flex" justifyContent="space-between" mt={4} pt={3} borderTop="1px solid" borderColor="divider">
                <Box>
                  {activeStep > 0 && (
                    <Button onClick={() => setActiveStep(activeStep - 1)}>
                      Back
                    </Button>
                  )}
                </Box>
                <Box display="flex" gap={1}>
                  {activeStep < 2 && (
                    <Button variant="contained" onClick={() => setActiveStep(activeStep + 1)}>
                      Next
                    </Button>
                  )}
                  {activeStep === 2 && (
                    <>
                      <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => handleSavePaper(PaperStatus.DRAFT)}>
                        Save as Draft
                      </Button>
                      <Button 
                        variant="contained" 
                        startIcon={<PublishIcon />} 
                        onClick={handlePublish}
                        disabled={
                          !paperForm.title ||
                          !paperForm.classId ||
                          !paperForm.subjectId ||
                          sections.length === 0 ||
                          !sections.some(section => section.questions && section.questions.length > 0) ||
                          (paperForm.totalMarks > 0 && currentMarks !== paperForm.totalMarks) ||
                          nonUniformSelectionSections.length > 0
                        }
                      >
                        Publish
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        <PaperListView
          papers={papers}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          centers={centers}
          classes={visibleFilterClasses}
          subjects={visibleFilterSubjects}
          selectedCenter={selectedCenter}
          selectedClass={selectedClass}
          selectedSubject={selectedSubject}
          selectedStatus={selectedStatus}
          searchQuery={searchQuery}
          isSystemAdmin={isSystemAdmin}
          canEditModule={canEditModule}
          canViewModule={canViewModule}
          onCenterChange={handleCenterChange}
          onClassChange={handleClassChange}
          onSubjectChange={handleSubjectChange}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onExportPDF={handleExportPDF}
          onChangeStatus={handleChangeStatus}
          onSyncFromBank={handleSyncPaperFromBank}
          onCreateNew={handleCreateNew}
        />
      )}

      {/* Custom Question Dialog */}
      <CustomQuestionDialog
        open={customQuestionDialogOpen}
        onClose={() => {
          setCustomQuestionDialogOpen(false);
          setEditingQuestionIndex(null);
          setPendingSubParent(null);
        }}
        onSave={handleSaveCustomQuestion}
        editingIndex={editingQuestionIndex}
        initialData={customQuestionInitialData}
        chapters={chapters}
        hasClassAndSubject={!!paperForm.classId && !!paperForm.subjectId}
      />

      {/* Sync-from-Bank preview dialog — opens on the paper-level sync
          action from the list menu or the builder header button. Shows a
          dry-run of exactly which questions will change before the
          author confirms the bulk overwrite. */}
      <SyncPreviewDialog
        open={syncPreviewOpen}
        onClose={handleCloseSyncPreview}
        onApply={handleApplySyncFromBank}
        paperTitle={syncPreviewPaper?.title || ''}
        preview={syncPreview}
        loading={syncPreviewLoading}
        applying={syncPreviewApplying}
        error={syncPreviewError}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => confirmAction()}
        title={confirmTitle}
        message={confirmMessage}
        loading={confirmLoading}
      />
    </Box>
  );
};

export default GenerateQuestionPaperPage;
